import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MyAccountBottomNav from '../components/MyAccountBottomNav';
import { DSBadge } from '../design-system/primitives';
import useMyMembership, { GUEST_PASS_COST, CREDIT_COST } from '../hooks/useMyMembership';
import HomeTab from './myMembership/HomeTab';
import StandingTab from './myMembership/StandingTab';
import AssignedTab, { buildAssignedGroups } from './myMembership/AssignedTab';
import ProfileTab from './myMembership/ProfileTab';
import MembershipSheet from './myMembership/MembershipSheet';
import MembershipCardSheet from './myMembership/MembershipCardSheet';
import PinGolfHandicapAudit from '../components/PinGolfHandicapAudit';
import SigningHistorySheet from './myMembership/SigningHistorySheet';
import { money, displayNameFor, formatDay, formatPhone } from './myMembership/helpers';
import { phoneError, normalizePhone } from '../utils/phone';
import { TIER_NAMES } from '../constants/techTiers';
import './MyMembership.css';
import UserAvatar from '../components/UserAvatar';

// The member-facing home base, and the app's default landing page after login.
//
// Four tabs plus one always-reachable card, all rendering from this one
// component and selected by the URL. The identity header is persistent across
// every tab: it is the member's card in the club, and it is also where a
// negative balance escalates to, so it cannot live on one tab only.
//
// The old four-tab split (Account / Credit / Tech / Badges) is kept as aliases.
// See docs/MY_MEMBERSHIP_REQUIREMENTS.md.

const VIEWS = {
  '/my-membership': 'home',
  '/my-membership/credit': 'home',
  '/my-membership/standing': 'standing',
  '/my-membership/badges': 'standing',
  '/my-membership/assigned': 'assigned',
  '/my-membership/profile': 'profile',
  '/my-membership/tech': 'profile'
};

const PRONOUN_OPTIONS = ['She/Her', 'He/Him', 'They/Them', 'She/They', 'He/They', 'Prefer not to say'];

// key -> what the profile field is called on the user record, what to call it in
// the sheet, and the help line. Keeping this as data means every row edits the
// same way and there is one place to change the copy.
const FIELD_DEFS = {
  // Not every account is created with a name. Somebody who signed up at a
  // machine after posting a score gave an email address and a password and
  // nothing else, which is deliberate, but it left them with no way to say who
  // they are afterwards. The server has always accepted these from the member
  // themselves; only the rows were missing.
  firstName: {
    label: 'First name',
    userKey: 'first_name',
    placeholder: 'Your first name',
    help: 'Used on your membership card and anywhere the club shows your name.'
  },
  lastName: {
    label: 'Last name',
    userKey: 'last_name',
    placeholder: 'Your last name'
  },
  preferredName: {
    label: 'Preferred name',
    userKey: 'preferred_name',
    placeholder: 'What the club calls you'
  },
  pronouns: {
    label: 'Pronouns',
    userKey: 'pronouns',
    options: PRONOUN_OPTIONS,
    placeholder: 'Type your pronouns',
    body: 'Shown next to your name across the club app.',
    help: 'Pick one, or choose Something else to type your own.'
  },
  phoneNumber: {
    label: 'Phone',
    userKey: 'phone_number',
    type: 'tel',
    inputMode: 'tel',
    placeholder: 'Optional',
    help: 'Used only if a host needs to reach you about an event.',
    maxLength: 20,
    // The sheet opens on the formatted number, so what the member edits is what
    // they see on the row. It saves normalized (bare digits, or + and a country
    // code) and formatPhone puts the brackets back for display.
    format: formatPhone,
    validate: phoneError,
    normalize: normalizePhone
  },
  sternUsername: {
    label: 'Stern Insider username',
    userKey: 'stern_username',
    placeholder: 'Required for competitions',
    help: 'Must match your Stern Insider account exactly.'
  },
  matchplayUserId: {
    label: 'MatchPlay user ID',
    userKey: 'matchplay_user_id',
    placeholder: 'Optional',
    help: 'Saving pulls your IFPA number across automatically.'
  },
  ifpaPlayerId: {
    label: 'IFPA player ID',
    userKey: 'ifpa_player_id',
    placeholder: 'Optional'
  }
};

function MyMembership() {
  const location = useLocation();
  const navigate = useNavigate();
  const view = VIEWS[location.pathname] || 'home';

  const membership = useMyMembership();
  const {
    currentUser, membershipSettings, onboardingStatus, loading, error, signedDocuments,
    devices, devicesLoading, transactions, transactionsLoading,
    badges, taskStats, placements, golfSummary, matchplayProfile,
    assigned, assignedLoading, busy,
    balance, tabEnabled, isNegative, purchasesBlocked,
    addMachineCredit, buyGuestPass, donate, saveField, refreshUser, toggleGameDownAlerts
  } = membership;

  const [sheet, setSheet] = useState(null);
  const [cardOpen, setCardOpen] = useState(false);
  // Owns its own fetch, so opening the audit never costs the tab a request it usually wastes.
  const [handicapOpen, setHandicapOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isMember = !!currentUser?.is_member;
  const hasApplication = !!onboardingStatus?.application
    && !isMember
    && onboardingStatus.application.status !== 'closed';

  // The three onboarding steps, in the order they happen. The meta line is the
  // completion date when done, and the honest waiting copy when not: two of the
  // three are performed by an admin in person, and the applicant's real job is
  // to wait and show up.
  const onboardingSteps = useMemo(() => {
    const onboarding = onboardingStatus?.onboarding || {};
    const subscriptionDone = !!(onboarding.subscriptionActive || onboarding.paymentSetupComplete);
    return [
      {
        label: 'Orientation',
        done: !!onboarding.orientationCompleted,
        doneMeta: onboarding.orientationCompletedAt
          ? `Completed ${formatDay(onboarding.orientationCompletedAt)}`
          : 'Complete',
        todoMeta: 'Our team will reach out to schedule a time with you.'
      },
      {
        label: 'Subscription setup',
        done: subscriptionDone,
        doneMeta: 'Active',
        todoMeta: 'An admin will set this up with you at the club.'
      },
      {
        label: 'Master lock setup',
        done: !!onboarding.masterLockSetup,
        doneMeta: 'Complete',
        todoMeta: 'Done in person on your first visit after orientation.'
      }
    ];
  }, [onboardingStatus]);

  const assignedGroups = useMemo(
    () => buildAssignedGroups(assigned, currentUser?.tech_tier ?? 0),
    [assigned, currentUser]
  );
  const assignedCount = assignedGroups.reduce((count, group) => count + group.rows.length, 0);

  const closeSheet = () => setSheet(null);

  // Every money action runs through here so the sheet closes only once the
  // server has answered, and the confirm button cannot be pressed twice.
  const runFromSheet = async (action) => {
    setSubmitting(true);
    const ok = await action();
    setSubmitting(false);
    if (ok !== false) {
      closeSheet();
    }
  };

  // Shared by every purchase sheet: what it costs against what you have, plus
  // the two insufficient-funds branches. With a tab the member opts into going
  // further negative; without one the confirm button is removed entirely rather
  // than left to fail on the server.
  const purchaseSheet = ({ title, price, priceLabel, confirmLabel, fields, note, validate, onConfirm }) => ({
    title,
    body: `Balance ${money(balance)} · costs ${priceLabel || money(price)}`,
    fields,
    note,
    // The tab checkbox is a real gate, not a notice. Unticking it is the member
    // saying "not on my tab", and without this the purchase would go through on
    // the tab anyway, since the server decides that from tab_enabled.
    validate: (values) => {
      if (isNegative && tabEnabled && !values.useTab) {
        return 'This purchase goes on your tab. Tick the box to go ahead, or settle up with a host first.';
      }
      return validate ? validate(values) : '';
    },
    blocked: purchasesBlocked ? {
      title: 'Not enough credit',
      body: 'Ask a host at the desk to add credit to your balance.'
    } : null,
    check: isNegative && tabEnabled ? {
      key: 'useTab',
      label: 'Use my tab (balance goes further negative)',
      defaultChecked: true
    } : null,
    confirmLabel: confirmLabel || `Confirm ${money(price)}`,
    onConfirm
  });

  const openPurchase = (key) => {
    if (key === 'guestPass') {
      setSheet(purchaseSheet({
        title: 'Guest day pass',
        price: GUEST_PASS_COST,
        fields: [{ key: 'guestName', label: "Guest's name", placeholder: 'Required' }],
        validate: (values) => (values.guestName.trim() ? '' : "Enter the guest's name"),
        onConfirm: (values) => runFromSheet(() => buyGuestPass(values.guestName))
      }));
      return;
    }

    if (key === 'donation') {
      setSheet(purchaseSheet({
        title: 'Send a donation',
        priceLabel: 'what you choose',
        confirmLabel: 'Send donation',
        fields: [{ key: 'amount', label: 'Amount', value: '10.00', type: 'number', inputMode: 'decimal' }],
        note: 'Donations pay for parts, machine upkeep, snacks, supplies, and other expenses.',
        validate: (values) => (parseFloat(values.amount) > 0 ? '' : 'Enter an amount above zero'),
        onConfirm: (values) => runFromSheet(() => donate(values.amount))
      }));
    }
  };

  // The one-tap machine credit still confirms. It is the most frequent action in
  // the app, so it is a single sheet with a single button, not a nested dialog.
  const confirmMachineCredit = (device) => {
    setSheet(purchaseSheet({
      title: `Add credit to ${device.device_name}`,
      price: CREDIT_COST,
      onConfirm: () => runFromSheet(() => addMachineCredit(device))
    }));
  };

  const openFieldSheet = (key) => {
    const def = FIELD_DEFS[key];
    setSheet({
      title: `Edit ${def.label.charAt(0).toLowerCase()}${def.label.slice(1)}`,
      body: def.body || 'Saved to your profile straight away.',
      fields: [{
        key,
        label: def.label,
        value: (def.format ? def.format(currentUser[def.userKey]) : currentUser[def.userKey]) || '',
        placeholder: def.placeholder,
        help: def.help,
        type: def.type,
        inputMode: def.inputMode,
        options: def.options,
        maxLength: def.maxLength
      }],
      validate: def.validate ? (values) => def.validate(values[key]) : undefined,
      confirmLabel: 'Save',
      onConfirm: (values) => runFromSheet(async () => {
        const value = def.normalize ? def.normalize(values[key]) : values[key];
        const saved = await saveField(key, value);
        return saved !== null;
      })
    });
  };

  const openBioSheet = () => {
    setSheet({
      title: 'Edit your bio',
      body: 'Attendees read this on every event you host.',
      fields: [{
        key: 'hostProfile',
        label: 'Bio',
        value: currentUser.host_profile || '',
        placeholder: 'What should attendees know about you?',
        type: 'textarea'
      }],
      confirmLabel: 'Save bio',
      onConfirm: (values) => runFromSheet(async () => {
        const saved = await saveField('hostProfile', values.hostProfile);
        return saved !== null;
      })
    });
  };

  const openBadgeSheet = (badge) => {
    setSheet({
      title: badge.name,
      body: badge.description,
      note: badge.earned_at ? `Earned ${formatDay(badge.earned_at)}.` : null,
      cancelLabel: 'Close'
    });
  };

  const openPlacementSheet = (placement) => {
    const position = placement.isLive ? 'Currently' : 'You finished';
    setSheet({
      title: placement.name,
      body: `${position} ${placement.place} of ${placement.playerCount} players.`,
      note: placement.isGrandChampion
        ? 'You took the Grand Champion title for this season.'
        : placement.isLive
          ? `${placement.roundsPlayed} of ${placement.roundsTotal} weeks scored so far.`
          : null,
      cancelLabel: 'Close'
    });
  };

  if (loading) {
    return <div className="mm-page"><p className="mm-empty">Loading...</p></div>;
  }

  if (!currentUser) {
    return (
      <div className="mm-page">
        <p className="mm-error">Failed to load your membership. Try again in a moment.</p>
      </div>
    );
  }

  const tier = currentUser.tech_tier ?? 0;

  return (
    <div className="mm-page ds-bottom-nav-page">
      <div className="mm-column">
        <header className="mm-identity">
          <UserAvatar person={currentUser} className="mm-identity__avatar" />
          <div className="mm-identity__main">
            <div className="mm-identity__nameline">
              <h1 className="mm-identity__name">{displayNameFor(currentUser)}</h1>
              {currentUser.pronouns && (
                <span className="mm-identity__pronouns">{currentUser.pronouns}</span>
              )}
            </div>
            <div className="mm-identity__chips">
              <DSBadge variant={isMember ? 'success' : hasApplication ? 'info' : 'neutral'} size="sm">
                {isMember ? 'Member' : hasApplication ? 'Applicant' : 'Guest'}
              </DSBadge>
              {isMember && tier > 0 && (
                <DSBadge variant="neutral" size="sm">Tier {tier} · {TIER_NAMES[tier]}</DSBadge>
              )}
            </div>
          </div>
        </header>

        {error && <p className="mm-error">{error}</p>}

        <div className="mm-cards">
          {view === 'home' && (
            <HomeTab
              currentUser={currentUser}
              membershipSettings={membershipSettings}
              onboardingSteps={onboardingSteps}
              isMember={isMember}
              hasApplication={hasApplication}
              balance={balance}
              isNegative={isNegative}
              tabEnabled={tabEnabled}
              purchasesBlocked={purchasesBlocked}
              devices={devices}
              devicesLoading={devicesLoading}
              transactions={transactions}
              transactionsLoading={transactionsLoading}
              showAllActivity={showAllActivity}
              onToggleActivity={() => setShowAllActivity((prev) => !prev)}
              badges={badges}
              busy={busy}
              onAddCredit={confirmMachineCredit}
              onOpenPurchase={openPurchase}
              onOpenBadge={openBadgeSheet}
              onGoProfile={() => navigate('/my-membership/profile')}
              onGoStanding={() => navigate('/my-membership/standing')}
            />
          )}

          {view === 'standing' && (
            <StandingTab
              currentUser={currentUser}
              badges={badges}
              taskStats={taskStats}
              placements={placements}
              golfSummary={golfSummary}
              matchplayProfile={matchplayProfile}
              onOpenBadge={openBadgeSheet}
              onOpenPlacement={openPlacementSheet}
              onOpenHandicap={() => setHandicapOpen(true)}
            />
          )}

          {view === 'assigned' && (
            <AssignedTab
              groups={assignedGroups}
              loading={assignedLoading}
              onNavigate={navigate}
            />
          )}

          {view === 'profile' && (
            <ProfileTab
              currentUser={currentUser}
              membershipSettings={membershipSettings}
              onboardingSteps={onboardingSteps}
              isMember={isMember}
              hasApplication={hasApplication}
              signedDocuments={signedDocuments}
              onOpenDocument={(key) => navigate(`/acknowledge-rules?document=${key}`)}
              onOpenHistory={() => setHistoryOpen(true)}
              onEditField={openFieldSheet}
              onEditBio={openBioSheet}
              onPhotoChange={refreshUser}
              onToggleGameDown={toggleGameDownAlerts}
              onApply={() => navigate('/apply')}
            />
          )}
        </div>
      </div>

      {sheet && (
        <MembershipSheet
          key={sheet.title}
          sheet={sheet}
          submitting={submitting}
          onClose={closeSheet}
        />
      )}

      {handicapOpen && (
        <PinGolfHandicapAudit onClose={() => setHandicapOpen(false)} />
      )}

      {historyOpen && (
        <SigningHistorySheet onClose={() => setHistoryOpen(false)} />
      )}

      {cardOpen && (
        <MembershipCardSheet
          currentUser={currentUser}
          membershipSettings={membershipSettings}
          isMember={isMember}
          hasApplication={hasApplication}
          onClose={() => setCardOpen(false)}
        />
      )}

      <MyAccountBottomNav
        onOpenCard={() => setCardOpen((prev) => !prev)}
        cardOpen={cardOpen}
        assignedCount={assignedCount}
        cardLabel={isMember ? 'Membership card' : 'Guest card'}
      />
    </div>
  );
}

export default MyMembership;
